import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Network, Server, Database, ArrowUpDown, CheckCircle, XCircle,
  AlertCircle, RefreshCw, Plus, Settings, Trash2, Link, Link2Off,
  FileText, Users, Activity, Clock, Download, Upload, Code,
  Shield, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InteroperabilityHub = () => {
  const [stats, setStats] = useState(null);
  const [connections, setConnections] = useState([]);
  const [supportedEmrs, setSupportedEmrs] = useState([]);
  const [exchangeLogs, setExchangeLogs] = useState([]);
  const [capabilities, setCapabilities] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddConnection, setShowAddConnection] = useState(false);
  
  const [newConnection, setNewConnection] = useState({
    name: '',
    type: 'epic',
    fhir_base_url: '',
    auth_type: 'oauth2',
    credentials: {},
    sync_direction: 'bidirectional',
    sync_schedule: 'daily'
  });

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadStats(),
        loadConnections(),
        loadSupportedEmrs(),
        loadExchangeLogs(),
        loadCapabilities()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase29/hub/stats`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadConnections = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase29/emr/connections`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setConnections(data.connections || []);
      }
    } catch (error) {
      console.error('Error loading connections:', error);
    }
  };

  const loadSupportedEmrs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase29/emr/supported`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSupportedEmrs(data.emrs || []);
      }
    } catch (error) {
      console.error('Error loading supported EMRs:', error);
    }
  };

  const loadExchangeLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase29/exchange/logs?limit=50`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setExchangeLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error loading exchange logs:', error);
    }
  };

  const loadCapabilities = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase29/hub/capabilities`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCapabilities(data);
      }
    } catch (error) {
      console.error('Error loading capabilities:', error);
    }
  };

  const addConnection = async () => {
    if (!newConnection.name || !newConnection.fhir_base_url) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/phase29/emr/connections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(newConnection)
      });

      if (response.ok) {
        toast.success('EMR connection added successfully');
        setShowAddConnection(false);
        loadConnections();
        setNewConnection({
          name: '',
          type: 'epic',
          fhir_base_url: '',
          auth_type: 'oauth2',
          credentials: {},
          sync_direction: 'bidirectional',
          sync_schedule: 'daily'
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add connection');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const testConnection = async (connectionId) => {
    try {
      const response = await fetch(`${API_URL}/api/phase29/emr/connections/${connectionId}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(`Connection verified! FHIR ${result.fhir_version}`);
        loadConnections();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const deleteConnection = async (connectionId) => {
    if (!confirm('Are you sure you want to delete this connection?')) return;

    try {
      const response = await fetch(`${API_URL}/api/phase29/emr/connections/${connectionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (response.ok) {
        toast.success('Connection deleted');
        loadConnections();
      } else {
        toast.error('Failed to delete connection');
      }
    } catch (error) {
      toast.error('Network error');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      connected: 'bg-green-100 text-green-800',
      pending_verification: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800',
      disconnected: 'bg-gray-100 text-gray-800'
    };
    return (
      <Badge className={styles[status] || styles.disconnected}>
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getEmrIcon = (type) => {
    // Return a consistent server icon for all EMR types
    return <Server className="h-6 w-6" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="hub-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="interoperability-hub">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Network className="h-7 w-7 text-teal-600" />
            Interoperability Hub
          </h1>
          <p className="text-gray-500">FHIR R4 compliant data exchange with external EMR systems</p>
        </div>
        <Dialog open={showAddConnection} onOpenChange={setShowAddConnection}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-connection-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add EMR Connection
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Add EMR Connection</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Connection Name *</Label>
                <Input
                  value={newConnection.name}
                  onChange={(e) => setNewConnection({...newConnection, name: e.target.value})}
                  placeholder="e.g., Main Hospital Epic"
                />
              </div>
              <div>
                <Label>EMR System *</Label>
                <Select
                  value={newConnection.type}
                  onValueChange={(v) => setNewConnection({...newConnection, type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {supportedEmrs.map((emr) => (
                      <SelectItem key={emr.id} value={emr.id}>
                        {emr.name} ({emr.fhir_version})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>FHIR Base URL *</Label>
                <Input
                  value={newConnection.fhir_base_url}
                  onChange={(e) => setNewConnection({...newConnection, fhir_base_url: e.target.value})}
                  placeholder="https://fhir.hospital.org/r4"
                />
              </div>
              <div>
                <Label>Authentication Type</Label>
                <Select
                  value={newConnection.auth_type}
                  onValueChange={(v) => setNewConnection({...newConnection, auth_type: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="oauth2">OAuth 2.0 (SMART on FHIR)</SelectItem>
                    <SelectItem value="api_key">API Key</SelectItem>
                    <SelectItem value="basic">Basic Auth</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sync Direction</Label>
                <Select
                  value={newConnection.sync_direction}
                  onValueChange={(v) => setNewConnection({...newConnection, sync_direction: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="import">Import Only</SelectItem>
                    <SelectItem value="export">Export Only</SelectItem>
                    <SelectItem value="bidirectional">Bidirectional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full" onClick={addConnection}>
                Add Connection
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Connections</p>
                  <p className="text-2xl font-bold">{stats.connections.active}</p>
                </div>
                <Link className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Exchanges</p>
                  <p className="text-2xl font-bold">{stats.exchanges.total}</p>
                </div>
                <ArrowUpDown className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Imports</p>
                  <p className="text-2xl font-bold">{stats.exchanges.imports}</p>
                </div>
                <Download className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Exports</p>
                  <p className="text-2xl font-bold">{stats.exchanges.exports}</p>
                </div>
                <Upload className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="connections">EMR Connections</TabsTrigger>
          <TabsTrigger value="logs">Exchange Logs</TabsTrigger>
          <TabsTrigger value="fhir">FHIR Resources</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* FHIR Capabilities */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  FHIR Capabilities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-500">FHIR Version</span>
                    <Badge className="bg-teal-100 text-teal-800">{stats?.fhir_version}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Supported Resources</p>
                    <div className="flex flex-wrap gap-2">
                      {stats?.supported_resources?.map((resource) => (
                        <Badge key={resource} variant="outline">{resource}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Supported EMRs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Server className="h-5 w-5" />
                  Supported EMR Systems
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {supportedEmrs.slice(0, 4).map((emr) => (
                    <div key={emr.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <Server className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium">{emr.name}</span>
                      </div>
                      <Badge variant="outline" className={
                        emr.status === 'supported' ? 'border-green-500 text-green-700' :
                        emr.status === 'beta' ? 'border-yellow-500 text-yellow-700' :
                        'border-gray-300'
                      }>
                        {emr.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Connections Tab */}
        <TabsContent value="connections" className="mt-4">
          {connections.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Link2Off className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No EMR connections configured</p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => setShowAddConnection(true)}
                >
                  Add Your First Connection
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {connections.map((conn) => (
                <Card key={conn.id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="p-3 bg-gray-100 rounded-lg">
                          {getEmrIcon(conn.type)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium">{conn.name}</h3>
                            {getStatusBadge(conn.status)}
                          </div>
                          <p className="text-sm text-gray-500">
                            {conn.fhir_base_url}
                          </p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                            <span className="capitalize">{conn.type}</span>
                            <span>•</span>
                            <span className="capitalize">{conn.auth_type}</span>
                            <span>•</span>
                            <span className="capitalize">{conn.sync_direction}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => testConnection(conn.id)}
                        >
                          <Zap className="h-4 w-4 mr-1" />
                          Test
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => deleteConnection(conn.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Logs Tab */}
        <TabsContent value="logs" className="mt-4">
          {exchangeLogs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No exchange logs yet</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Direction</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {exchangeLogs.map((log) => (
                        <tr key={log.id}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {log.direction === 'import' ? (
                                <Download className="h-4 w-4 text-purple-500" />
                              ) : (
                                <Upload className="h-4 w-4 text-orange-500" />
                              )}
                              <span className="capitalize">{log.direction}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">{log.resource_type}</td>
                          <td className="px-4 py-3">
                            {log.status === 'success' ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-500" />
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatDate(log.timestamp)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* FHIR Resources Tab */}
        <TabsContent value="fhir" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                FHIR Resource Endpoints
              </CardTitle>
              <CardDescription>
                Access patient data in FHIR R4 format
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { path: '/fhir/Patient/{id}', desc: 'Get patient in FHIR format' },
                  { path: '/fhir/Condition?patient={id}', desc: 'Get patient conditions' },
                  { path: '/fhir/MedicationStatement?patient={id}', desc: 'Get patient medications' },
                  { path: '/fhir/Encounter/{id}', desc: 'Get encounter/visit' },
                  { path: '/fhir/Bundle/patient/{id}', desc: 'Get complete patient bundle' }
                ].map((endpoint, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <code className="text-sm text-teal-600 font-mono">{endpoint.path}</code>
                      <p className="text-sm text-gray-500 mt-1">{endpoint.desc}</p>
                    </div>
                    <Badge variant="outline">GET</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default InteroperabilityHub;
